// src/pages/tablet/TabletLogin.tsx
// מסך כניסה לטאבלט - כניסה עם קוד גן, שם משתמש וסיסמה

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useAttendanceStore } from '../../stores/attendanceStore';
import { authApi } from '../../services/api';
import { getDeviceFingerprint } from '../../utils/deviceFingerprint';

// חילוץ claims מתוך JWT token (ללא אימות חתימה - רק parsing)
function parseJwtClaims(token: string): Record<string, any> {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return {};
    const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(payload);
  } catch {
    return {};
  }
}


export default function TabletLogin() {
  const navigate = useNavigate();
  const { setUser, setToken, setRefreshToken, setTokenExpiresAt, setRequiresAgreement } = useAuthStore();
  const { resetStore } = useAttendanceStore();

  useEffect(() => {
    document.title = 'קיפיקיד — גן ילדים';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#16a34a');
  }, []);

  const [code, setCode] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const upperCode = code.toUpperCase().trim();
  const needsCredentials = upperCode.length > 0;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!username.trim() || !password) {
        setError('יש להזין שם משתמש וסיסמה');
        setIsLoading(false);
        return;
      }

      const deviceFingerprint = await getDeviceFingerprint();
      const result = await authApi.staffLogin(username.trim(), password, upperCode, deviceFingerprint);

      if (result.success && result.data) {
        const data = result.data;

        resetStore();

        const token = data.accessToken || data.token;

        // חילוץ kindergartenPublicId מה-JWT אם חסר בתגובה
        let kgPublicId = data.user?.kindergartenPublicId || data.kindergartenPublicId;
        if (!kgPublicId && token) {
          const claims = parseJwtClaims(token);
          kgPublicId = claims.kindergartenPublicId;
          if (kgPublicId) {
            console.log('[TabletLogin] kindergartenPublicId extracted from JWT:', kgPublicId);
          }
        }

        // עדכון user עם kindergartenPublicId שחולץ מ-JWT
        setUser({
          id: data.user?.id?.toString() || data.user?.publicId || 'tablet-' + Date.now(),
          email: data.user?.email || `tablet@${upperCode.toLowerCase()}.local`,
          name: data.user?.firstName ? `${data.user.firstName} ${data.user.lastName || ''}`.trim() : `טאבלט - ${upperCode}`,
          role: 'tablet',
          kindergartenPublicId: kgPublicId,
          kindergartenName: data.user?.kindergartenName || data.kindergartenName || upperCode,
          kindergartenCode: upperCode,
          publicId: data.user?.publicId,
        });

        if (token) {
          setToken(token);
        }

        // שמירת refresh token ומועד תפוגה
        const refreshTokenValue = data.refreshToken || data.RefreshToken;
        if (refreshTokenValue) {
          setRefreshToken(refreshTokenValue);
        }
        const expiresAt = data.expiresAt || data.ExpiresAt;
        if (expiresAt) {
          setTokenExpiresAt(expiresAt);
        }

        // Check if agreement is required
        if (data.requiresAgreement) {
          setRequiresAgreement(true, data.agreementVersion);
          navigate('/agreement-approval', { replace: true });
          return;
        }

        navigate('/tablet/dashboard');
      } else {
        setError(result.message || 'שם משתמש, סיסמה או קוד גן שגויים');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setError(msg || 'שגיאת חיבור לשרת. בדקו את החיבור לאינטרנט');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '24px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          width: '100%',
          maxWidth: '400px',
          padding: '40px 30px',
        }}
      >
        {/* לוגו */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <img
            src="/kipikid-logo.png"
            alt="KipiKid"
            style={{
              width: '80px',
              height: '80px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto 12px',
            }}
          />
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#00BCD4', margin: 0 }}>
            KipiKid
          </h1>
          <p style={{ color: '#666', margin: '8px 0 0', fontSize: '14px' }}>
            ממשק טאבלט לגן
          </p>
        </div>

        <form onSubmit={handleLogin}>
          {/* קוד גן */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
              שם הגן / קוד גן
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError('');
              }}
              placeholder="לדוגמה: גן הפרחים או DEMO"
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '18px',
                border: '2px solid #e0e0e0',
                borderRadius: '12px',
                textAlign: 'center',
                fontWeight: '600',
                boxSizing: 'border-box',
              }}
              autoFocus
            />
          </div>

          {/* שדות username/password */}
          {needsCredentials && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  שם משתמש
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="טלפון / שם משתמש / אימייל"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '12px',
                    boxSizing: 'border-box',
                    direction: 'ltr',
                    textAlign: 'right',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333' }}>
                  סיסמה
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="סיסמה"
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '12px',
                    boxSizing: 'border-box',
                    direction: 'ltr',
                    textAlign: 'right',
                  }}
                />
              </div>
            </>
          )}

          {error && (
            <div style={{
              background: '#ffebee',
              color: '#c62828',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '16px',
              textAlign: 'center',
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !code.trim() || (needsCredentials && (!username.trim() || !password))}
            style={{
              width: '100%',
              padding: '16px',
              borderRadius: '12px',
              border: 'none',
              background: isLoading || !code.trim()
                ? '#ccc'
                : 'linear-gradient(135deg, #00BCD4 0%, #0097A7 100%)',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: isLoading || !code.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'מתחבר...' : 'כניסה למערכת'}
          </button>
        </form>

        <button
          onClick={() => navigate('/')}
          style={{
            display: 'block',
            margin: '20px auto 0',
            background: 'none',
            border: 'none',
            color: '#999',
            fontSize: '14px',
            cursor: 'pointer',
          }}
        >
          ← חזרה לדף הבית
        </button>
      </div>
    </div>
  );
}
